import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useCycle } from '@/lib/cycle';
import { LOGO_URL } from '@/lib/api';
import { LogOut, LayoutDashboard, Upload, Factory, Wrench, FlaskConical, BadgeIndianRupee, ShieldCheck, ShoppingCart, Users2, Leaf, GitBranch, ListTodo, Presentation, Settings as SettingsIcon, Building2, FileSpreadsheet, Contact, CalendarDays, Sparkles, Menu, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { SidebarItem, HeaderSelector, GradientButton, StatusBadge } from '@/components/design';
import { MONTHS } from '@/lib/theme';
import { useAccess } from '@/lib/access';

const NAV_GROUPS = [
  { label: 'Overview', items: [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard', tone: 'indigo' },
    { to: '/uploads', icon: Upload, label: 'Upload Center', tone: 'cyan' },
  ]},
  // `sections`: the user must hold ANY of these section keys (from the section master) to see the item.
  { label: 'Auto-Synced (JSON)', items: [
    { to: '/production', icon: Factory, label: 'Production', tone: 'cyan', sections: ['production'] },
    { to: '/maintenance', icon: Wrench, label: 'Maintenance', tone: 'blue', sections: ['maintenance'] },
    { to: '/npd', icon: FlaskConical, label: 'NPD Pipeline', tone: 'green', sections: ['npd'] },
  ]},
  { label: 'Manual Forms', items: [
    { to: '/business-sales', icon: BadgeIndianRupee, label: 'Business & Sales', tone: 'orange', sections: ['business_sales'] },
    { to: '/quality', icon: ShieldCheck, label: 'Quality', tone: 'blue', sections: ['quality'] },
    { to: '/change-points', icon: GitBranch, label: '4M Change Points', indent: true, tone: 'blue', sections: ['quality'] },
    { to: '/supply-chain', icon: ShoppingCart, label: 'Supply Chain', tone: 'orange', sections: ['supply_chain'] },
    { to: '/supply-chain/schedule-vs-supply', icon: FileSpreadsheet, label: 'Schedule vs Supply', indent: true, tone: 'orange', sections: ['schedule_vs_supply'] },
    { to: '/hr-safety', icon: Users2, label: 'People & Safety', tone: 'indigo', sections: ['hr_safety', 'hr'] },
    { to: '/environment', icon: Leaf, label: 'Environment', tone: 'green', sections: ['environment'] },
  ]},
  { label: 'Governance', items: [
    { to: '/actions', icon: ListTodo, label: 'Action Tracker', tone: 'purple' },
    { to: '/customers', icon: Contact, label: 'Customer Master', tone: 'red' },
    { to: '/generate-ppt', icon: Presentation, label: 'Generate PPT', tone: 'indigo', roles: ['admin', 'management'] },
    { to: '/settings', icon: SettingsIcon, label: 'Settings', adminOnly: true, tone: 'slate' },
  ]},
];

const cycleLabel = (c) => (c ? `${MONTHS[c.month]} ${c.year}` : 'Select cycle');

export default function Layout() {
  const { user, logout } = useAuth();
  const { can, catalogue, sections: mySections } = useAccess();
  const { units, cycles, currentUnit, currentCycle, selectUnit, selectCycle } = useCycle();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Close the drawer whenever the route changes.
  React.useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  React.useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  if (!user) { window.location.href = '/login'; return null; }

  const initials = (user.name || 'U').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  const sidebar = (
    <aside className="w-[260px] shrink-0 h-full flex flex-col bg-white/95 backdrop-blur-xl border-r border-[var(--aampl-border)]" style={{ boxShadow: '4px 0 24px rgba(30, 41, 59, 0.04)' }} data-testid="sidebar">
      <div className="px-5 pt-5 pb-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-white border border-[var(--aampl-border)] shadow-sm flex items-center justify-center overflow-hidden shrink-0">
          <img src={LOGO_URL} alt="AAMPL" className="w-9 h-9 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>
        <div className="min-w-0">
          <div className="font-extrabold text-[19px] leading-tight tracking-tight text-[var(--aampl-text)]">AAMPL</div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #4F6BFF, #8B5CF6)' }}>MRM System</div>
        </div>
        <button type="button" className="ml-auto lg:hidden p-2 rounded-xl hover:bg-slate-100 text-slate-500" onClick={() => setMobileOpen(false)} aria-label="Close menu"><X size={18} /></button>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-3">
        {NAV_GROUPS.filter((group) => group.items.some((it) => !(it.adminOnly && user.role !== 'admin') && !(it.roles && !it.roles.includes(user.role)) && !(it.sections && !can(...it.sections)))).map((group) => (
          <div key={group.label}>
            <div className="px-3 mb-1.5 eyebrow text-[10px] text-slate-400">{group.label}</div>
            <div className="space-y-1">
              {group.items.map((it) => {
                if (it.adminOnly && user.role !== 'admin') return null;
                if (it.roles && !it.roles.includes(user.role)) return null;
                if (it.sections && !can(...it.sections)) return null;
                const active = loc.pathname === it.to || (it.to !== '/' && loc.pathname.startsWith(it.to + '/'));
                const exactChildActive = it.to !== '/' && group.items.some((o) => o !== it && o.to.startsWith(it.to + '/') && loc.pathname === o.to);
                return (
                  <SidebarItem
                    key={it.to}
                    icon={it.icon}
                    label={it.label}
                    tone={it.tone}
                    indent={it.indent}
                    active={active && !exactChildActive}
                    onClick={() => nav(it.to)}
                    testId={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-[var(--aampl-border)]">
        <div className="flex items-center gap-3 px-2 py-2 rounded-2xl bg-[#F8FAFF] border border-[var(--aampl-border)]">
          <div className="avatar-grad w-10 h-10 text-sm shrink-0">{initials}</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate text-[var(--aampl-text)]">{user.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate" title={user.all_sections ? 'All sections' : (mySections.map((k) => (catalogue.find((c) => c.key === k) || {}).label || k).join(', ') || 'No sections')}>
              {user.role === 'admin' ? 'admin • all sections' : user.role === 'management' ? 'management • all sections (read)' : mySections.map((k) => (catalogue.find((c) => c.key === k) || {}).label || k).join(', ') || 'no sections'}
            </div>
          </div>
        </div>
        <button onClick={logout} className="mt-2 w-full text-sm font-medium flex items-center gap-2 justify-center py-2.5 rounded-xl border border-[var(--aampl-border)] bg-white text-slate-600 hover:text-[#DC2626] hover:bg-[#FFF1F2] hover:border-[#FECDD3] transition-all duration-200" data-testid="btn-logout"><LogOut size={15} /> Logout</button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-[var(--aampl-bg)] app-canvas overflow-hidden">
      <div className="hidden lg:flex h-full">{sidebar}</div>

      {/* Mobile / tablet drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div className="absolute inset-0 bg-[rgba(23,37,84,0.35)] backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 shadow-2xl">{sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="relative z-30 shrink-0 bg-white/80 backdrop-blur-xl border-b border-[var(--aampl-border)] px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap" style={{ boxShadow: '0 4px 20px rgba(30, 41, 59, 0.04)' }}>
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" className="lg:hidden p-2 -ml-1 rounded-xl border border-[var(--aampl-border)] bg-white text-slate-600 hover:bg-slate-50" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="btn-open-menu"><Menu size={18} /></button>
            <div className="min-w-0">
              <h1 className="text-[17px] sm:text-[20px] font-extrabold tracking-tight text-[var(--aampl-text)] leading-tight truncate">Management Review Meeting</h1>
              <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                <span>{currentCycle?.label || (currentUnit ? `No cycle for ${currentUnit.name}` : 'Select unit & cycle')}</span>
                {currentCycle && (
                  <>
                    <span className="text-slate-300">•</span>
                    <span className="inline-flex items-center gap-1.5">Status: <StatusBadge status={currentCycle.status} label={currentCycle.status} /></span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <HeaderSelector
              icon={Building2}
              label={currentUnit?.name || 'No unit'}
              items={units}
              selectedId={currentUnit?.id}
              onSelect={selectUnit}
              emptyText="No units accessible"
              testId="unit-switcher"
              itemTestId={(u) => `unit-option-${u.code}`}
              renderItem={(u) => (<><span>{u.name}</span><span className="text-[10px] uppercase font-bold text-[var(--aampl-primary)]">{u.code}</span></>)}
            />
            <HeaderSelector
              icon={CalendarDays}
              label={cycleLabel(currentCycle)}
              items={cycles.filter((c) => c.unit_id === currentUnit?.id)}
              selectedId={currentCycle?.id}
              onSelect={selectCycle}
              emptyText="No cycles for this unit"
              testId="cycle-selector"
              itemTestId={(c) => `cycle-option-${c.year}-${c.month}`}
              renderItem={(c) => (<><span>{cycleLabel(c)}</span><span className="text-[10px] uppercase text-slate-500">{c.status}</span></>)}
            />
            {user.role === 'admin' && <CreateCycleButton />}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function CreateCycleButton() {
  const { units, refresh, selectCycle } = useCycle();
  const [open, setOpen] = React.useState(false);
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [month, setMonth] = React.useState(new Date().getMonth() + 1);
  const [unitId, setUnitId] = React.useState('');
  const [bulk, setBulk] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  React.useEffect(() => { if (units.length && !unitId) setUnitId(units[0].id); }, [units, unitId]);

  const submit = async () => {
    setErr(''); setBusy(true);
    try {
      if (bulk) {
        const r = await api.post('/cycles/bulk', { year: Number(year), month: Number(month), unit_id: 'none' });
        toast.success(`Created ${r.data.created?.length || 0} cycle(s). Skipped: ${(r.data.skipped || []).join(', ') || 'none'}`);
      } else {
        if (!unitId) { setErr('Select a unit'); setBusy(false); return; }
        const r = await api.post('/cycles', { year: Number(year), month: Number(month), unit_id: unitId });
        selectCycle(r.data);
      }
      await refresh();
      setOpen(false);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to create cycle'); } finally { setBusy(false); }
  };
  return (
    <>
      <GradientButton icon={Sparkles} onClick={() => setOpen(true)} data-testid="btn-new-cycle">New Cycle</GradientButton>
      {open && createPortal(
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow mb-1">MRM Cycle</div>
            <h3 className="text-xl font-extrabold tracking-tight text-[var(--aampl-text)] mb-4">Create New MRM Cycle</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="aampl-label">Year</label><input type="number" className="aampl-input" value={year} onChange={(e)=>setYear(e.target.value)} data-testid="input-cycle-year" /></div>
                <div><label className="aampl-label">Month (1-12)</label><input type="number" min="1" max="12" className="aampl-input" value={month} onChange={(e)=>setMonth(e.target.value)} data-testid="input-cycle-month" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer p-3 border border-[var(--aampl-border)] rounded-xl bg-[#F8FAFF]">
                <input type="checkbox" checked={bulk} onChange={(e) => setBulk(e.target.checked)} data-testid="input-bulk-cycle" />
                <span>Create for <b>all active units</b> at once</span>
              </label>
              {!bulk && (
                <div>
                  <label className="aampl-label">Unit</label>
                  <select className="aampl-input" value={unitId} onChange={(e) => setUnitId(e.target.value)} data-testid="input-cycle-unit">
                    {units.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}
                  </select>
                </div>
              )}
              {err && <div className="text-[#DC2626] text-sm bg-[#FFF1F2] border border-[#FECDD3] rounded-xl px-3 py-2">{err}</div>}
              <div className="flex gap-2 justify-end pt-2">
                <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
                <button className="btn-primary" onClick={submit} disabled={busy} data-testid="btn-create-cycle-submit">{busy ? 'Creating…' : (bulk ? 'Create All' : 'Create')}</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
